import { useMemo, useState, type ReactNode } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Tooltip,
} from "@fluentui/react-components";
import {
  annualPayOf,
  bonusWarnings,
  COST_CATEGORY_LABEL,
  inEffect,
  overridesOn,
  PAY_TYPE_LABEL,
  PAY_UNIT,
  isFlatPay,
  rateOn,
  today,
  type BonusOverrideRecord,
  type BonusType,
  type CostItemRecord,
  type Entered,
  type RateCard,
  type RecordKind,
  addMonths,
  monthOf,
} from "../../../core";
import { useApi, type HistoryResponse, type PersonResponse, type ReferenceResponse } from "../api";
import { DateInput } from "../DateInput";
import { cents, count, day, hours, money, perHour, when } from "../format";
import { go, href } from "../router";
import { ErrorBar, Fact, Loading, Page, useMe, Warnings } from "../ui";
import { ArchiveDialog, BonusLetterDialog, BonusOverrideDialog, EndRecordDialog, ProfileDialog, RecordDialog, VoidDialog } from "./PersonDialogs";
import { Switcher } from "./Switcher";
import { ClientAliasesPanel } from "./Clients";

type Open =
  | { kind: "record"; recordKind: RecordKind }
  | { kind: "void"; recordId: string; label: string }
  | { kind: "end"; recordId: string; label: string; noun: "cost" | "override" }
  | { kind: "letter" }
  | { kind: "override" }
  | { kind: "profile" }
  | { kind: "archive" }
  | null;

/** The person switcher: names only, so moving between cards doesn't fetch everyone's pay. */
function PersonSwitcher({ current }: { current: string }) {
  const people = useApi<{ people: { id: string; name: string; status: string }[] }>("/people?view=names");
  const items = (people.data?.people ?? []).map((p) => ({ id: p.id, text: p.name, group: p.status === "archived" ? "Archived" : "People" }));
  return <Switcher label="Go to another person" items={items} current={current} onPick={(pid) => go("people", pid)} />;
}

export function PersonCard({ id }: { id: string }) {
  const me = useMe();
  const [asOf, setAsOf] = useState(today());
  const data = useApi<PersonResponse>(`/people/${encodeURIComponent(id)}?asOf=${asOf}`);
  const ref = useApi<ReferenceResponse>("/reference");
  const history = useApi<HistoryResponse>(`/people/${encodeURIComponent(id)}/history`);
  const [open, setOpen] = useState<Open>(null);
  const editable = me.can("people.edit");
  const types = ref.data?.bonusTypes ?? [];

  const close = (changed: boolean) => {
    setOpen(null);
    if (changed) {
      data.reload();
      history.reload();
    }
  };

  if (!data.data) {
    return (
      <Page title="Person">
        <div className="page-nav">
          <a href={href("people")} className="back">
            ← People
          </a>
          <PersonSwitcher current={id} />
        </div>
        {data.loading ? <Loading /> : <ErrorBar message={data.error} />}
      </Page>
    );
  }

  const { person, rateCard, rateHistory } = data.data;
  const roleName = ref.data?.roles.roles.find((r) => r.id === person.roleId)?.name;
  const archived = person.status === "archived";

  return (
    <Page
      title={person.name}
      actions={
        editable && (
          <>
            <Button onClick={() => setOpen({ kind: "profile" })}>Edit profile</Button>
            <Button onClick={() => setOpen({ kind: "archive" })}>{archived ? "Restore…" : "Archive…"}</Button>
          </>
        )
      }
    >
      <div className="page-nav">
        <a href={href("people")} className="back">
          ← People
        </a>
        <PersonSwitcher current={id} />
      </div>
      <ErrorBar message={data.error} />
      <div className="card-grid profile-grid">
        <Card className="panel">
          <Subtitle2>Profile</Subtitle2>
          <div className="facts stacked">
            <Fact label="Status">
              {archived ? (
                <Badge appearance="outline">Archived · last day {day(person.endDate)}</Badge>
              ) : person.endDate ? (
                `Active · last day ${day(person.endDate)}`
              ) : (
                "Active"
              )}
            </Fact>
            <Fact label="Title">{person.title || "—"}</Fact>
            <Fact label="Role">{roleName ?? "—"}</Fact>
            <Fact label="3Seven account">{person.email || "—"}</Fact>
            <Fact label="Time zone">{person.timeZone.replace("America/", "").replace("_", " ")}</Fact>
            <Fact label="Pay IDs in old workbooks">{person.payIds.length ? person.payIds.join(", ") : "—"}</Fact>
          </div>
        </Card>
        <RatesPanel card={rateCard} asOf={asOf} onAsOf={setAsOf} />
      </div>

      <RecordSection
        title="Pay"
        kind="pay"
        records={person.compRecords}
        asOf={asOf}
        onAdd={editable ? () => setOpen({ kind: "record", recordKind: "comp" }) : undefined}
        headers={["From", "Pay type", "Amount", "Note"]}
        cells={(r) => [day(r.from), PAY_TYPE_LABEL[r.type], isFlatPay(r.type) ? `${money(r.amount)}${PAY_UNIT[r.type]}` : perHour(r.amount), r.note ?? ""]}
        label={(r) => `pay from ${day(r.from)}`}
        onVoid={editable ? (recordId, label) => setOpen({ kind: "void", recordId, label }) : undefined}
      />
      <RecordSection
        title="Associated costs"
        kind="cost"
        records={person.costItems}
        asOf={asOf}
        onAdd={editable ? () => setOpen({ kind: "record", recordKind: "costItem" }) : undefined}
        headers={["From", "To", "Cost", "Amount"]}
        cells={(r) => [day(r.from), r.to ? day(r.to) : "Open", `${r.label} · ${COST_CATEGORY_LABEL[r.category]}`, costAmount(r)]}
        label={(r) => `${r.label} from ${day(r.from)}`}
        inEffectFor={(rs, d) => rs.filter((r) => !r.voided && r.from <= d && (!r.to || r.to >= d))}
        onVoid={editable ? (recordId, label) => setOpen({ kind: "void", recordId, label }) : undefined}
        onEnd={editable ? (recordId, label) => setOpen({ kind: "end", recordId, label, noun: "cost" }) : undefined}
        endLabel="End this cost…"
      />
      <BonusSection
        person={person}
        card={rateCard}
        types={types}
        asOf={asOf}
        onLetter={editable ? () => setOpen({ kind: "letter" }) : undefined}
        onOverride={editable ? () => setOpen({ kind: "override" }) : undefined}
        onVoid={editable ? (recordId, label) => setOpen({ kind: "void", recordId, label }) : undefined}
        onEnd={editable ? (recordId, label) => setOpen({ kind: "end", recordId, label, noun: "override" }) : undefined}
      />
      <div className="card-grid">
        <RecordSection
          title="Capacity and PTO"
          kind="capacity"
          records={person.capacity}
          asOf={asOf}
          onAdd={editable ? () => setOpen({ kind: "record", recordKind: "capacity" }) : undefined}
          headers={["From", "Hours a week", "PTO days"]}
          cells={(r) => [day(r.from), count(r.hoursPerWeek), count(r.ptoDays)]}
          label={(r) => `capacity from ${day(r.from)}`}
          onVoid={editable ? (recordId, label) => setOpen({ kind: "void", recordId, label }) : undefined}
        />
        <RecordSection
          title="Home work state"
          kind="state"
          records={person.locations}
          asOf={asOf}
          onAdd={editable ? () => setOpen({ kind: "record", recordKind: "location" }) : undefined}
          headers={["From", "State"]}
          cells={(r) => [day(r.from), r.state]}
          label={(r) => `work state from ${day(r.from)}`}
          onVoid={editable ? (recordId, label) => setOpen({ kind: "void", recordId, label }) : undefined}
        />
        <RecordSection
          title="Travel: work in another state"
          kind="travel"
          records={person.travel ?? []}
          asOf={asOf}
          inEffectFor={(records, on) => records.filter((r) => !r.voided && r.from <= on && r.to >= on)}
          onAdd={editable ? () => setOpen({ kind: "record", recordKind: "travel" }) : undefined}
          addLabel="Add travel"
          headers={["From", "To", "Working in", "Note"]}
          cells={(r) => [day(r.from), day(r.to), r.state, r.note ?? ""]}
          label={(r) => `travel ${day(r.from)} to ${day(r.to)}`}
          onVoid={editable ? (recordId, label) => setOpen({ kind: "void", recordId, label }) : undefined}
        />
      </div>

      <ActualRates personId={id} />

      <ClientAliasesPanel personId={id} personName={person.name} />

      <div className="card-grid">
        <Card className="panel">
          <Subtitle2>Rate history</Subtitle2>
          <Caption1>Every date the hourly rate changes, through the end of {Number(asOf.slice(0, 4)) + 2}.</Caption1>
          <RateHistory base={rateHistory.base} ote={rateHistory.ote} />
        </Card>
        <Card className="panel">
          <Subtitle2>History</Subtitle2>
          <Caption1>Every change to this card: who made it and when.</Caption1>
          {history.loading && !history.data ? <Loading /> : <HistoryList entries={history.data?.entries ?? []} />}
        </Card>
      </div>

      {open?.kind === "record" && <RecordDialog person={person} kind={open.recordKind} onClose={close} />}
      {open?.kind === "void" && <VoidDialog person={person} recordId={open.recordId} label={open.label} onClose={close} />}
      {open?.kind === "end" && <EndRecordDialog person={person} recordId={open.recordId} label={open.label} noun={open.noun} onClose={close} />}
      {open?.kind === "letter" && <BonusLetterDialog person={person} types={types} letter={rateCard.bonus.letter ?? inEffect(person.bonusTargets ?? [], today())} onClose={close} />}
      {open?.kind === "override" && <BonusOverrideDialog person={person} types={types} current={rateCard.bonus} onClose={close} />}
      {open?.kind === "profile" && <ProfileDialog person={person} roles={ref.data?.roles.roles ?? []} onClose={close} />}
      {open?.kind === "archive" && <ArchiveDialog person={person} onClose={close} />}
    </Page>
  );
}

/**
 * BP2–BP3 on the card: the bonus in effect on the as-of day, by type, with OTE per the letter and with
 * overrides; then the letters and overrides themselves.
 */
function BonusSection(props: {
  person: PersonResponse["person"];
  card: RateCard;
  types: BonusType[];
  asOf: string;
  onLetter?: () => void;
  onOverride?: () => void;
  onVoid?: (recordId: string, label: string) => void;
  onEnd?: (recordId: string, label: string) => void;
}) {
  const { person, card, types, asOf } = props;
  const bonus = card.bonus;
  const comp = inEffect(person.compRecords, asOf);
  const pay = comp ? annualPayOf(comp) : null;
  const typeName = (id: string | null) => (id === null ? "Whole bonus" : (types.find((t) => t.id === id)?.name ?? id));
  const empty = bonus.lines.length === 0 && bonus.totalOverride === null;
  return (
    <div className="card-grid">
      <Card className="panel">
        <div className="panel-head">
          <Subtitle2>Bonus on {day(asOf)}</Subtitle2>
          {(props.onLetter || props.onOverride) && (
            <span className="panel-tools">
              {props.onLetter && (
                <Button size="small" onClick={props.onLetter}>
                  New letter…
                </Button>
              )}
              {props.onOverride && (
                <Button size="small" onClick={props.onOverride}>
                  Override…
                </Button>
              )}
            </span>
          )}
        </div>
        {empty ? (
          <Body1 className="muted">No compensation letter in effect. Enter the targets from {person.name}'s letter with New letter.</Body1>
        ) : (
          <Table size="extra-small" className="data-table" aria-label="Bonus by type">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Bonus type</TableHeaderCell>
                <TableHeaderCell className="num">Target</TableHeaderCell>
                <TableHeaderCell className="num">Override</TableHeaderCell>
                <TableHeaderCell className="num">Counts</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bonus.lines.map((l) => (
                <TableRow key={l.typeId}>
                  <TableCell>
                    {l.name}
                    {!l.includedInOte && <Caption1 className="block muted">not in OTE</Caption1>}
                  </TableCell>
                  <TableCell className="num">{money(l.target)}</TableCell>
                  <TableCell className="num">{l.override === null ? "—" : <OverrideBadge amount={l.override} />}</TableCell>
                  <TableCell className="num">{bonus.totalOverride === null ? money(l.amount) : <span className="muted">{money(l.amount)}</span>}</TableCell>
                </TableRow>
              ))}
              {bonus.totalOverride !== null && (
                <TableRow>
                  <TableCell>Whole-bonus override</TableCell>
                  <TableCell className="num">—</TableCell>
                  <TableCell className="num">
                    <OverrideBadge amount={bonus.totalOverride} />
                  </TableCell>
                  <TableCell className="num">{money(bonus.totalOverride)}</TableCell>
                </TableRow>
              )}
              <TableRow className="total">
                <TableCell>Bonus in OTE</TableCell>
                <TableCell className="num">{money(bonus.targetTotal)}</TableCell>
                <TableCell />
                <TableCell className="num">{money(bonus.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )}
        <div className="facts">
          <Fact label="OTE per the letter">{pay === null ? "—" : money(pay + bonus.targetTotal)}</Fact>
          <Fact label="OTE with overrides">{pay === null ? "—" : money(pay + bonus.total)}</Fact>
        </div>
        {bonus.letter && (
          <Caption1 className="muted">
            Letter from {day(bonus.letter.from)}
            {bonus.letter.note ? `: ${bonus.letter.note}` : ""}.
          </Caption1>
        )}
        <Warnings items={bonusWarnings(bonus, types, !!comp)} />
      </Card>
      <div className="stack">
        <RecordSection
          title="Compensation letters"
          kind="letter"
          records={person.bonusTargets ?? []}
          asOf={asOf}
          onAdd={props.onLetter}
          addLabel="Add letter"
          headers={["From", "Targets", "Note"]}
          cells={(r) => [
            day(r.from),
            r.targets.length
              ? r.targets.map((t) => (
                  <span key={t.typeId} className="block nowrap">
                    {typeName(t.typeId)} {money(t.amount)}
                  </span>
                ))
              : "No bonus",
            r.note ?? "",
          ]}
          label={(r) => `letter from ${day(r.from)}`}
          onVoid={props.onVoid}
        />
        <RecordSection
          title="Bonus overrides"
          kind="override"
          records={person.bonusOverrides ?? []}
          asOf={asOf}
          onAdd={props.onOverride}
          addLabel="Add override"
          headers={["From", "Until", "Applies to", "Amount", "Reason"]}
          cells={(r) => [day(r.from), r.to ? day(r.to) : "Open", typeName(r.typeId), `${money(r.amount)}/yr`, r.note ?? ""]}
          label={(r) => `${typeName(r.typeId).toLowerCase()} override from ${day(r.from)}`}
          inEffectFor={(rs: BonusOverrideRecord[], d) => overridesOn(rs, d)}
          onVoid={props.onVoid}
          onEnd={props.onEnd}
          endLabel="End this override…"
        />
      </div>
    </div>
  );
}

function OverrideBadge({ amount }: { amount: number }) {
  return (
    <span className="override">
      <Badge appearance="tint" color="warning" size="small">
        Override
      </Badge>
      {money(amount)}
    </span>
  );
}

function costAmount(r: CostItemRecord) {
  if (r.frequency === "monthly") return `${cents(r.amount)}/mo`;
  if (r.frequency === "annual") return `${cents(r.amount)}/yr`;
  return `${cents(r.amount)} once, over ${r.amortizeMonths ?? 12} mo`;
}

function RatesPanel({ card, asOf, onAsOf }: { card: RateCard; asOf: string; onAsOf: (d: string) => void }) {
  const base = card.components.filter((c) => c.group === "base");
  const bonus = card.components.filter((c) => c.group === "bonus");
  const b = card.basis;
  return (
    <Card className="panel rates">
      <div className="panel-head">
        <Subtitle2>Cost rates</Subtitle2>
        <label className="inline-field">
          <Caption1>As of</Caption1>
          <DateInput type="date" size="small" value={asOf} onChange={(_, d) => d.value && onAsOf(d.value)} />
        </label>
      </div>
      <div className="facts">
        <Fact label="Base rate">
          <span className="big">{card.type ? perHour(card.baseRate) : "—"}</span>
        </Fact>
        <Fact label="OTE rate (with target bonus)">
          <span className="big">{card.type ? perHour(card.oteRate) : "—"}</span>
        </Fact>
        <Fact label="Loaded cost a year">
          {card.type ? `${money(card.loadedBase)} Base · ${money(card.loadedOte)} OTE` : "—"}
        </Fact>
      </div>
      <Caption1>
        {hours(card.productiveHours)} productive hours in {card.day.slice(0, 4)} = {count(b.capacityPerWeek / 5)} h a day × ({b.weekdays} weekdays −{" "}
        {b.holidays} holidays − {b.ptoDays} PTO days)
      </Caption1>
      <Warnings items={card.warnings} />
      {card.type && (
        <Table size="extra-small" className="data-table buildup" aria-label="Rate build-up">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Component</TableHeaderCell>
              <TableHeaderCell className="num">A year</TableHeaderCell>
              <TableHeaderCell className="num">An hour</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {base.map((c) => (
              <BuildUpRow key={c.key} label={c.label} annual={c.annual} hourly={c.perHour} />
            ))}
            <BuildUpRow label="Loaded cost, Base" annual={card.loadedBase} hourly={card.baseRate} total />
            {bonus.map((c) => (
              <BuildUpRow key={c.key} label={c.label} annual={c.annual} hourly={c.perHour} />
            ))}
            {bonus.length > 0 && <BuildUpRow label="Loaded cost, OTE" annual={card.loadedOte} hourly={card.oteRate} total />}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function BuildUpRow({ label, annual, hourly, total }: { label: string; annual: number; hourly: number; total?: boolean }) {
  return (
    <TableRow className={total ? "total" : undefined}>
      <TableCell>{label}</TableCell>
      <TableCell className="num">{cents(annual)}</TableCell>
      <TableCell className="num">{cents(hourly)}</TableCell>
    </TableRow>
  );
}

function RecordSection<T extends Entered & { from: string }>(props: {
  title: string;
  kind: string;
  records: T[];
  asOf: string;
  headers: string[];
  cells: (r: T) => ReactNode[];
  label: (r: T) => string;
  onAdd?: () => void;
  addLabel?: string;
  onVoid?: (recordId: string, label: string) => void;
  onEnd?: (recordId: string, label: string) => void;
  endLabel?: string;
  inEffectFor?: (records: T[], day: string) => T[];
}) {
  const [showVoided, setShowVoided] = useState(false);
  const sorted = useMemo(
    () => [...props.records].sort((a, b) => b.from.localeCompare(a.from) || b.enteredAt.localeCompare(a.enteredAt)),
    [props.records],
  );
  const current = new Set((props.inEffectFor ? props.inEffectFor(props.records, props.asOf) : [inEffect(props.records, props.asOf)]).filter(Boolean).map((r) => r!.id));
  const voidedCount = props.records.filter((r) => r.voided).length;
  const rows = sorted.filter((r) => showVoided || !r.voided);
  return (
    <Card className="panel">
      <div className="panel-head">
        <Subtitle2>{props.title}</Subtitle2>
        <span className="panel-tools">
          {voidedCount > 0 && (
            <Button size="small" appearance="subtle" onClick={() => setShowVoided(!showVoided)}>
              {showVoided ? "Hide withdrawn" : `Show withdrawn (${voidedCount})`}
            </Button>
          )}
          {props.onAdd && (
            <Button size="small" onClick={props.onAdd}>
              {props.addLabel ?? "Add change"}
            </Button>
          )}
        </span>
      </div>
      {rows.length === 0 ? (
        <Body1 className="muted">Nothing on file.</Body1>
      ) : (
        <Table size="extra-small" className="data-table" aria-label={props.title}>
          <TableHeader>
            <TableRow>
              {props.headers.map((h) => (
                <TableHeaderCell key={h}>{h}</TableHeaderCell>
              ))}
              <TableHeaderCell>Entered</TableHeaderCell>
              <TableHeaderCell aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className={r.voided ? "voided" : undefined}>
                {props.cells(r).map((c, i) => (
                  <TableCell key={i}>
                    {c}
                    {i === 0 && current.has(r.id) && (
                      <Badge appearance="tint" color="brand" size="small" className="in-effect">
                        In effect
                      </Badge>
                    )}
                    {i === 0 && r.voided && (
                      <Tooltip content={`Withdrawn by ${r.voided.by} on ${when(r.voided.at)}: ${r.voided.reason}`} relationship="description">
                        <Badge appearance="outline" size="small" className="in-effect">
                          Withdrawn
                        </Badge>
                      </Tooltip>
                    )}
                  </TableCell>
                ))}
                <TableCell>
                  <Caption1>
                    {r.enteredBy}, {when(r.enteredAt)}
                  </Caption1>
                </TableCell>
                <TableCell>
                  {!r.voided && (props.onVoid || props.onEnd) && (
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button size="small" appearance="subtle" aria-label={`Actions for ${props.label(r)}`}>
                          ⋯
                        </Button>
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {props.onEnd && !(r as unknown as { to?: string }).to && <MenuItem onClick={() => props.onEnd!(r.id, props.label(r))}>{props.endLabel ?? "End…"}</MenuItem>}
                          {props.onVoid && <MenuItem onClick={() => props.onVoid!(r.id, props.label(r))}>Withdraw (entered in error)…</MenuItem>}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}

function RateHistory({ base, ote }: { base: { from: string; rate: number }[]; ote: { from: string; rate: number }[] }) {
  const dates = [...new Set([...base, ...ote].map((s) => s.from))].sort();
  if (!dates.length) return <Body1 className="muted">No pay on file yet.</Body1>;
  return (
    <Table size="extra-small" className="data-table" aria-label="Rate history">
      <TableHeader>
        <TableRow>
          <TableHeaderCell>From</TableHeaderCell>
          <TableHeaderCell className="num">Base</TableHeaderCell>
          <TableHeaderCell className="num">OTE</TableHeaderCell>
        </TableRow>
      </TableHeader>
      <TableBody>
        {dates.map((d) => (
          <TableRow key={d}>
            <TableCell>{day(d)}</TableCell>
            <TableCell className="num">{perHour(rateOn(base, d))}</TableCell>
            <TableCell className="num">{perHour(rateOn(ote, d))}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function HistoryList({ entries }: { entries: HistoryResponse["entries"] }) {
  if (!entries.length) return <Body1 className="muted">No changes yet.</Body1>;
  return (
    <ul className="history">
      {entries.map((e, i) => (
        <li key={i}>
          <Caption1>
            {when(e.at)} · {e.who}
          </Caption1>
          <Body1>{e.summary ?? e.action}</Body1>
        </li>
      ))}
    </ul>
  );
}

/**
 * Actual rate by month (EC14, rev. 1.16): what the person cost each month from their comp plan, over
 * the hours they reported, beside the calculated rate. Flat-paid people who work long months cost less
 * per hour; short months show unreported cost.
 */
function ActualRates({ personId }: { personId: string }) {
  const me = useMe();
  const t = new Date().toISOString().slice(0, 10);
  const from = `${addMonths(monthOf(t), -5)}-01`; // the last six months, this one included
  const perf = useApi<{ people?: { month: string; cost: number; reported: number; expected: number; rate: number; calculatedRate: number; unreported: number; through: string }[] }>(
    me.can("plans.read") && me.can("financials.read") ? `/performance?by=person&id=${encodeURIComponent(personId)}&from=${from}&to=${t}&zoom=month` : null,
  );
  const rows = perf.data?.people ?? [];
  if (!rows.length) return null;
  return (
    <Card className="panel wide">
      <Subtitle2>Actual rate by month</Subtitle2>
      <Caption1>
        What this person cost each month, from their comp plan, over the hours they reported. A flat-paid month with more hours than expected costs each
        project less per hour; fewer hours than expected leaves unreported cost. The month in progress counts to today.
      </Caption1>
      <table className="edit-table compact">
        <thead>
          <tr>
            <th>Month</th>
            <th className="num">Cost</th>
            <th className="num">Reported h</th>
            <th className="num">Expected h</th>
            <th className="num">Actual rate</th>
            <th className="num">Calculated rate</th>
            <th className="num">Unreported</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.month}>
              <td>{r.month}</td>
              <td className="num">{money(r.cost)}</td>
              <td className="num">{count(r.reported)}</td>
              <td className="num">{count(r.expected)}</td>
              <td className="num">{perHour(r.rate)}</td>
              <td className="num">{perHour(r.calculatedRate)}</td>
              <td className="num">{r.unreported > 0.5 ? money(r.unreported) : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
